import React, { useState, Fragment } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeftIcon, SaveIcon, EyeIcon, SendIcon, ChevronRightIcon, UsersIcon, FileTextIcon, EditIcon, BoldIcon, ItalicIcon, UnderlineIcon, ListIcon, LinkIcon as LinkIconLucide, ImageIcon } from 'lucide-react';
const NewsletterEditor = () => {
  const {
    id
  } = useParams();
  const isEditing = !!id;
  const [currentStep, setCurrentStep] = useState(1);
  const [newsletterTitle, setNewsletterTitle] = useState(isEditing ? 'June Newsletter Draft' : '');
  const [selectedTemplate, setSelectedTemplate] = useState(isEditing ? 'Monthly Updates' : '');
  const [subjectLine, setSubjectLine] = useState(isEditing ? 'Your Monthly Update - June 2023' : '');
  const [previewText, setPreviewText] = useState(isEditing ? 'Check out what is new this month...' : '');
  const [selectedRecipients, setSelectedRecipients] = useState(isEditing ? 'all-entrepreneurs' : '');
  const [newsletterContent, setNewsletterContent] = useState(isEditing ? '<h2>Welcome to Our Monthly Update</h2><p>Here are the latest highlights from our community...</p>' : '');
  const templates = [{
    id: '1',
    name: 'Monthly Updates',
    description: 'Standard template for monthly newsletters'
  }, {
    id: '2',
    name: 'Yearly Reports',
    description: 'Annual report and summary template'
  }, {
    id: '3',
    name: 'DC/UNO Updates',
    description: 'Updates for District Commissioners and Upazila Nirbahi Officers'
  }, {
    id: '4',
    name: 'Custom Theme',
    description: 'Create a custom themed newsletter'
  }];
  const recipientGroups = [{
    id: 'all-entrepreneurs',
    name: 'All Entrepreneurs',
    count: 3852
  }, {
    id: 'dc',
    name: 'DC',
    count: 64
  }, {
    id: 'uno',
    name: 'UNO',
    count: 492
  }, {
    id: 'custom',
    name: 'Custom Selection',
    count: 0
  }];
  const steps = [{
    number: 1,
    name: 'Details',
    icon: <FileTextIcon size={16} data-id="element-100" />
  }, {
    number: 2,
    name: 'Content',
    icon: <EditIcon size={16} data-id="element-101" />
  }, {
    number: 3,
    name: 'Recipients',
    icon: <UsersIcon size={16} data-id="element-102" />
  }, {
    number: 4,
    name: 'Review & Send',
    icon: <SendIcon size={16} data-id="element-103" />
  }];
  return <div className="space-y-6" data-id="element-104">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between" data-id="element-105">
        <div className="flex items-center" data-id="element-106">
          <Link to="/newsletters" className="p-1 mr-3 rounded-md text-secondary-500 hover:bg-secondary-100" data-id="element-107">
            <ArrowLeftIcon size={20} data-id="element-108" />
          </Link>
          <h2 className="text-2xl font-bold text-secondary-900" data-id="element-109">
            {isEditing ? 'Edit Newsletter' : 'Create Newsletter'}
          </h2>
        </div>
        <div className="flex space-x-3 mt-3 sm:mt-0" data-id="element-110">
          <button className="btn btn-outline flex items-center" data-id="element-111">
            <SaveIcon size={16} className="mr-1" data-id="element-112" />
            <span data-id="element-113">Save Draft</span>
          </button>
          {currentStep === 4 && <button className="btn btn-primary flex items-center" data-id="element-114">
              <SendIcon size={16} className="mr-1" data-id="element-115" />
              <span data-id="element-116">Send Now</span>
            </button>}
        </div>
      </div>
      {/* Progress Steps */}
      <div className="card" data-id="element-117">
        <div className="flex items-center justify-between" data-id="element-118">
          {steps.map((step, index) => <Fragment key={step.number} data-id="element-119">
              <button onClick={() => setCurrentStep(step.number)} className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-colors ${currentStep === step.number ? 'bg-primary-50 text-primary-700' : currentStep > step.number ? 'text-green-600' : 'text-secondary-500'}`} data-id="element-120">
                <div className={`flex items-center justify-center w-8 h-8 rounded-full ${currentStep === step.number ? 'bg-primary-600 text-white' : currentStep > step.number ? 'bg-green-600 text-white' : 'bg-secondary-200 text-secondary-600'}`} data-id="element-121">
                  {currentStep > step.number ? '✓' : step.icon}
                </div>
                <span className="font-medium hidden sm:inline" data-id="element-122">
                  {step.name}
                </span>
              </button>
              {index < steps.length - 1 && <div className="flex-1 h-0.5 bg-secondary-200 mx-2" data-id="element-123" />}
            </Fragment>)}
        </div>
      </div>
      {/* Step 1: Details */}
      {currentStep === 1 && <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-124">
          <div className="lg:col-span-2 space-y-6" data-id="element-125">
            <div className="card" data-id="element-126">
              <h3 className="text-lg font-semibold text-secondary-900 mb-4" data-id="element-127">
                Newsletter Details
              </h3>
              <div className="space-y-4" data-id="element-128">
                <div data-id="element-129">
                  <label htmlFor="newsletterTitle" className="block text-sm font-medium text-secondary-700 mb-1" data-id="element-130">
                    Newsletter Title *
                  </label>
                  <input id="newsletterTitle" type="text" value={newsletterTitle} onChange={e => setNewsletterTitle(e.target.value)} placeholder="Enter newsletter title (internal use only)" className="input" required data-id="element-131" />
                  <p className="text-xs text-secondary-500 mt-1" data-id="element-132">
                    This is for internal reference only and will not be visible
                    to recipients
                  </p>
                </div>
                <div data-id="element-133">
                  <label htmlFor="subjectLine" className="block text-sm font-medium text-secondary-700 mb-1" data-id="element-134">
                    Email Subject Line *
                  </label>
                  <input id="subjectLine" type="text" value={subjectLine} onChange={e => setSubjectLine(e.target.value)} placeholder="Enter email subject line" className="input" required data-id="element-135" />
                </div>
                <div data-id="element-136">
                  <label htmlFor="previewText" className="block text-sm font-medium text-secondary-700 mb-1" data-id="element-137">
                    Preview Text
                  </label>
                  <input id="previewText" type="text" value={previewText} onChange={e => setPreviewText(e.target.value)} placeholder="Enter preview text (appears in email inbox)" className="input" data-id="element-138" />
                  <p className="text-xs text-secondary-500 mt-1" data-id="element-139">
                    This text appears in the inbox preview next to the subject
                    line
                  </p>
                </div>
              </div>
            </div>
            <div className="card" data-id="element-140">
              <h3 className="text-lg font-semibold text-secondary-900 mb-4" data-id="element-141">
                Select Template
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-142">
                {templates.map(template => <button key={template.id} onClick={() => setSelectedTemplate(template.name)} className={`text-left p-4 border-2 rounded-lg transition-all ${selectedTemplate === template.name ? 'border-primary-600 bg-primary-50' : 'border-secondary-200 hover:border-secondary-300'}`} data-id="element-143">
                    <div className="flex items-start justify-between" data-id="element-144">
                      <div data-id="element-145">
                        <h4 className="font-medium text-secondary-900" data-id="element-146">
                          {template.name}
                        </h4>
                        <p className="text-sm text-secondary-500 mt-1" data-id="element-147">
                          {template.description}
                        </p>
                      </div>
                      {selectedTemplate === template.name && <div className="flex items-center justify-center w-6 h-6 rounded-full bg-primary-600 text-white" data-id="element-148">
                          ✓
                        </div>}
                    </div>
                  </button>)}
              </div>
            </div>
          </div>
          <div className="lg:col-span-1" data-id="element-149">
            <div className="card sticky top-6" data-id="element-150">
              <h3 className="text-lg font-semibold text-secondary-900 mb-4" data-id="element-151">
                Next Steps
              </h3>
              <div className="space-y-3" data-id="element-152">
                <div className="flex items-start space-x-3" data-id="element-153">
                  <div className="flex items-center justify-center w-6 h-6 rounded-full bg-primary-600 text-white text-xs" data-id="element-154">
                    1
                  </div>
                  <div data-id="element-155">
                    <p className="text-sm font-medium text-secondary-900" data-id="element-156">
                      Complete newsletter details
                    </p>
                    <p className="text-xs text-secondary-500" data-id="element-157">
                      Add title, subject line, and select template
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3" data-id="element-158">
                  <div className="flex items-center justify-center w-6 h-6 rounded-full bg-secondary-200 text-secondary-600 text-xs" data-id="element-159">
                    2
                  </div>
                  <div data-id="element-160">
                    <p className="text-sm font-medium text-secondary-900" data-id="element-161">
                      Customize content
                    </p>
                    <p className="text-xs text-secondary-500" data-id="element-162">
                      Edit template sections and add your content
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3" data-id="element-163">
                  <div className="flex items-center justify-center w-6 h-6 rounded-full bg-secondary-200 text-secondary-600 text-xs" data-id="element-164">
                    3
                  </div>
                  <div data-id="element-165">
                    <p className="text-sm font-medium text-secondary-900" data-id="element-166">
                      Select recipients
                    </p>
                    <p className="text-xs text-secondary-500" data-id="element-167">
                      Choose who will receive this newsletter
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3" data-id="element-168">
                  <div className="flex items-center justify-center w-6 h-6 rounded-full bg-secondary-200 text-secondary-600 text-xs" data-id="element-169">
                    4
                  </div>
                  <div data-id="element-170">
                    <p className="text-sm font-medium text-secondary-900" data-id="element-171">
                      Review and send
                    </p>
                    <p className="text-xs text-secondary-500" data-id="element-172">
                      Preview and send immediately
                    </p>
                  </div>
                </div>
              </div>
              <button onClick={() => setCurrentStep(2)} className="btn btn-primary w-full mt-6 flex items-center justify-center" disabled={!newsletterTitle || !subjectLine || !selectedTemplate} data-id="element-173">
                <span data-id="element-174">Continue to Content</span>
                <ChevronRightIcon size={16} className="ml-1" data-id="element-175" />
              </button>
            </div>
          </div>
        </div>}
      {/* Step 2: Content Editor */}
      {currentStep === 2 && <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-176">
          <div className="lg:col-span-2" data-id="element-177">
            <div className="card" data-id="element-178">
              <h3 className="text-lg font-semibold text-secondary-900 mb-4" data-id="element-179">
                Customize Content
              </h3>
              <p className="text-secondary-500 mb-4" data-id="element-180">
                Edit your newsletter content using the selected template:{' '}
                <strong data-id="element-181">{selectedTemplate}</strong>
              </p>
              {/* Text Editor Toolbar */}
              <div className="border border-secondary-300 rounded-t-md bg-secondary-50 p-2 flex items-center space-x-1" data-id="element-182">
                <button className="p-2 rounded hover:bg-secondary-200 text-secondary-700" title="Bold" data-id="element-183">
                  <BoldIcon size={18} data-id="element-184" />
                </button>
                <button className="p-2 rounded hover:bg-secondary-200 text-secondary-700" title="Italic" data-id="element-185">
                  <ItalicIcon size={18} data-id="element-186" />
                </button>
                <button className="p-2 rounded hover:bg-secondary-200 text-secondary-700" title="Underline" data-id="element-187">
                  <UnderlineIcon size={18} data-id="element-188" />
                </button>
                <div className="w-px h-6 bg-secondary-300 mx-2" data-id="element-189"></div>
                <button className="p-2 rounded hover:bg-secondary-200 text-secondary-700" title="Bullet List" data-id="element-190">
                  <ListIcon size={18} data-id="element-191" />
                </button>
                <button className="p-2 rounded hover:bg-secondary-200 text-secondary-700" title="Insert Link" data-id="element-192">
                  <LinkIconLucide size={18} data-id="element-193" />
                </button>
                <button className="p-2 rounded hover:bg-secondary-200 text-secondary-700" title="Insert Image" data-id="element-194">
                  <ImageIcon size={18} data-id="element-195" />
                </button>
                <div className="w-px h-6 bg-secondary-300 mx-2" data-id="element-196"></div>
                <select className="text-sm border border-secondary-300 rounded px-2 py-1 bg-white" data-id="element-197">
                  <option data-id="element-198">Paragraph</option>
                  <option data-id="element-199">Heading 1</option>
                  <option data-id="element-200">Heading 2</option>
                  <option data-id="element-201">Heading 3</option>
                </select>
              </div>
              {/* Content Editor */}
              <textarea value={newsletterContent} onChange={e => setNewsletterContent(e.target.value)} className="w-full min-h-[400px] p-4 border border-t-0 border-secondary-300 rounded-b-md focus:outline-none focus:ring-2 focus:ring-primary-500" placeholder="Start writing your newsletter content here...&#10;&#10;You can format text, add images, and create engaging content for your recipients." data-id="element-202" />
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md" data-id="element-203">
                <p className="text-sm text-blue-800" data-id="element-204">
                  <strong data-id="element-205">Tip:</strong> Use the toolbar above to format your
                  content. You can add headings, bold text, lists, links, and
                  images to make your newsletter more engaging.
                </p>
              </div>
            </div>
            <div className="flex justify-between mt-6" data-id="element-206">
              <button onClick={() => setCurrentStep(1)} className="btn btn-outline flex items-center" data-id="element-207">
                <ArrowLeftIcon size={16} className="mr-1" data-id="element-208" />
                <span data-id="element-209">Back to Details</span>
              </button>
              <button onClick={() => setCurrentStep(3)} className="btn btn-primary flex items-center" data-id="element-210">
                <span data-id="element-211">Continue to Recipients</span>
                <ChevronRightIcon size={16} className="ml-1" data-id="element-212" />
              </button>
            </div>
          </div>
          <div className="lg:col-span-1" data-id="element-213">
            <div className="card sticky top-6" data-id="element-214">
              <h3 className="text-lg font-semibold text-secondary-900 mb-4" data-id="element-215">
                Content Preview
              </h3>
              <div className="border border-secondary-200 rounded-lg p-4 bg-white min-h-[200px]" data-id="element-216">
                <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{
              __html: newsletterContent || '<p className="text-secondary-400">Your content will appear here...</p>'
            }} data-id="element-217" />
              </div>
              <div className="mt-4" data-id="element-218">
                <p className="text-xs text-secondary-500" data-id="element-219">
                  This is a preview of how your content will appear in the
                  newsletter. The actual email will include your template
                  styling.
                </p>
              </div>
            </div>
          </div>
        </div>}
      {/* Step 3: Recipients */}
      {currentStep === 3 && <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-220">
          <div className="lg:col-span-2" data-id="element-221">
            <div className="card" data-id="element-222">
              <h3 className="text-lg font-semibold text-secondary-900 mb-4" data-id="element-223">
                Select Recipients
              </h3>
              <div className="space-y-3" data-id="element-224">
                {recipientGroups.map(group => <button key={group.id} onClick={() => setSelectedRecipients(group.id)} className={`w-full text-left p-4 border-2 rounded-lg transition-all ${selectedRecipients === group.id ? 'border-primary-600 bg-primary-50' : 'border-secondary-200 hover:border-secondary-300'}`} data-id="element-225">
                    <div className="flex items-center justify-between" data-id="element-226">
                      <div className="flex items-center space-x-3" data-id="element-227">
                        <div className={`flex items-center justify-center w-5 h-5 rounded-full border-2 ${selectedRecipients === group.id ? 'border-primary-600 bg-primary-600' : 'border-secondary-300'}`} data-id="element-228">
                          {selectedRecipients === group.id && <div className="w-2 h-2 rounded-full bg-white" data-id="element-229"></div>}
                        </div>
                        <div data-id="element-230">
                          <h4 className="font-medium text-secondary-900" data-id="element-231">
                            {group.name}
                          </h4>
                          <p className="text-sm text-secondary-500" data-id="element-232">
                            {group.count.toLocaleString()} recipients
                          </p>
                        </div>
                      </div>
                      <UsersIcon size={20} className="text-secondary-400" data-id="element-233" />
                    </div>
                  </button>)}
              </div>
              <div className="flex justify-between mt-6" data-id="element-234">
                <button onClick={() => setCurrentStep(2)} className="btn btn-outline flex items-center" data-id="element-235">
                  <ArrowLeftIcon size={16} className="mr-1" data-id="element-236" />
                  <span data-id="element-237">Back to Content</span>
                </button>
                <button onClick={() => setCurrentStep(4)} className="btn btn-primary flex items-center" disabled={!selectedRecipients} data-id="element-238">
                  <span data-id="element-239">Continue to Review</span>
                  <ChevronRightIcon size={16} className="ml-1" data-id="element-240" />
                </button>
              </div>
            </div>
          </div>
          <div className="lg:col-span-1" data-id="element-241">
            <div className="card sticky top-6" data-id="element-242">
              <h3 className="text-lg font-semibold text-secondary-900 mb-4" data-id="element-243">
                Recipient Summary
              </h3>
              {selectedRecipients && <div className="space-y-3" data-id="element-244">
                  <div className="p-3 bg-primary-50 rounded-md" data-id="element-245">
                    <p className="text-sm font-medium text-primary-900" data-id="element-246">
                      Selected Group
                    </p>
                    <p className="text-lg font-semibold text-primary-700" data-id="element-247">
                      {recipientGroups.find(g => g.id === selectedRecipients)?.name}
                    </p>
                  </div>
                  <div className="flex justify-between items-center" data-id="element-248">
                    <span className="text-sm text-secondary-600" data-id="element-249">
                      Total Recipients
                    </span>
                    <span className="font-semibold text-secondary-900" data-id="element-250">
                      {recipientGroups.find(g => g.id === selectedRecipients)?.count.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between items-center" data-id="element-251">
                    <span className="text-sm text-secondary-600" data-id="element-252">
                      Estimated Delivery Time
                    </span>
                    <span className="font-semibold text-secondary-900" data-id="element-253">
                      ~5 minutes
                    </span>
                  </div>
                </div>}
              {!selectedRecipients && <p className="text-sm text-secondary-500" data-id="element-254">
                  Select a recipient group to see the summary
                </p>}
            </div>
          </div>
        </div>}
      {/* Step 4: Review & Send */}
      {currentStep === 4 && <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-255">
          <div className="lg:col-span-2 space-y-6" data-id="element-256">
            <div className="card" data-id="element-257">
              <h3 className="text-lg font-semibold text-secondary-900 mb-4" data-id="element-258">
                Review Newsletter
              </h3>
              <div className="space-y-4" data-id="element-259">
                <div className="flex justify-between items-start p-3 bg-secondary-50 rounded-md" data-id="element-260">
                  <div data-id="element-261">
                    <p className="text-sm font-medium text-secondary-700" data-id="element-262">
                      Newsletter Title
                    </p>
                    <p className="text-secondary-900" data-id="element-263">{newsletterTitle}</p>
                  </div>
                  <button onClick={() => setCurrentStep(1)} className="text-primary-600 hover:text-primary-700 text-sm" data-id="element-264">
                    Edit
                  </button>
                </div>
                <div className="flex justify-between items-start p-3 bg-secondary-50 rounded-md" data-id="element-265">
                  <div data-id="element-266">
                    <p className="text-sm font-medium text-secondary-700" data-id="element-267">
                      Subject Line
                    </p>
                    <p className="text-secondary-900" data-id="element-268">{subjectLine}</p>
                  </div>
                  <button onClick={() => setCurrentStep(1)} className="text-primary-600 hover:text-primary-700 text-sm" data-id="element-269">
                    Edit
                  </button>
                </div>
                <div className="flex justify-between items-start p-3 bg-secondary-50 rounded-md" data-id="element-270">
                  <div data-id="element-271">
                    <p className="text-sm font-medium text-secondary-700" data-id="element-272">
                      Preview Text
                    </p>
                    <p className="text-secondary-900" data-id="element-273">{previewText}</p>
                  </div>
                  <button onClick={() => setCurrentStep(1)} className="text-primary-600 hover:text-primary-700 text-sm" data-id="element-274">
                    Edit
                  </button>
                </div>
                <div className="flex justify-between items-start p-3 bg-secondary-50 rounded-md" data-id="element-275">
                  <div data-id="element-276">
                    <p className="text-sm font-medium text-secondary-700" data-id="element-277">
                      Template
                    </p>
                    <p className="text-secondary-900" data-id="element-278">{selectedTemplate}</p>
                  </div>
                  <button onClick={() => setCurrentStep(1)} className="text-primary-600 hover:text-primary-700 text-sm" data-id="element-279">
                    Edit
                  </button>
                </div>
                <div className="flex justify-between items-start p-3 bg-secondary-50 rounded-md" data-id="element-280">
                  <div data-id="element-281">
                    <p className="text-sm font-medium text-secondary-700" data-id="element-282">
                      Recipients
                    </p>
                    <p className="text-secondary-900" data-id="element-283">
                      {recipientGroups.find(g => g.id === selectedRecipients)?.name}{' '}
                      (
                      {recipientGroups.find(g => g.id === selectedRecipients)?.count.toLocaleString()}{' '}
                      recipients)
                    </p>
                  </div>
                  <button onClick={() => setCurrentStep(3)} className="text-primary-600 hover:text-primary-700 text-sm" data-id="element-284">
                    Edit
                  </button>
                </div>
              </div>
            </div>
            <div className="card" data-id="element-285">
              <h3 className="text-lg font-semibold text-secondary-900 mb-4" data-id="element-286">
                Content Preview
              </h3>
              <div className="border border-secondary-200 rounded-lg p-6 bg-white" data-id="element-287">
                <div className="text-center mb-4" data-id="element-288">
                  <div className="w-16 h-16 bg-primary-600 mx-auto mb-3" data-id="element-289"></div>
                  <h2 className="text-xl font-semibold" data-id="element-290">{subjectLine}</h2>
                  <p className="text-sm text-secondary-500 mt-1" data-id="element-291">
                    {previewText}
                  </p>
                </div>
                <div className="border-t border-secondary-200 pt-4" data-id="element-292">
                  <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{
                __html: newsletterContent
              }} data-id="element-293" />
                </div>
              </div>
            </div>
            <div className="flex justify-between" data-id="element-294">
              <button onClick={() => setCurrentStep(3)} className="btn btn-outline flex items-center" data-id="element-295">
                <ArrowLeftIcon size={16} className="mr-1" data-id="element-296" />
                <span data-id="element-297">Back to Recipients</span>
              </button>
            </div>
          </div>
          <div className="lg:col-span-1" data-id="element-298">
            <div className="card sticky top-6" data-id="element-299">
              <h3 className="text-lg font-semibold text-secondary-900 mb-4" data-id="element-300">
                Send Options
              </h3>
              <div className="space-y-4" data-id="element-301">
                <button className="w-full btn btn-primary flex items-center justify-center" data-id="element-302">
                  <SendIcon size={16} className="mr-2" data-id="element-303" />
                  <span data-id="element-304">Send Now</span>
                </button>
                <div className="pt-4 border-t border-secondary-200" data-id="element-305">
                  <h4 className="text-sm font-medium text-secondary-700 mb-2" data-id="element-306">
                    Before sending:
                  </h4>
                  <ul className="space-y-2 text-sm text-secondary-600" data-id="element-307">
                    <li className="flex items-start" data-id="element-308">
                      <span className="text-green-600 mr-2" data-id="element-309">✓</span>
                      <span data-id="element-310">Subject line is clear and engaging</span>
                    </li>
                    <li className="flex items-start" data-id="element-311">
                      <span className="text-green-600 mr-2" data-id="element-312">✓</span>
                      <span data-id="element-313">Preview text is set</span>
                    </li>
                    <li className="flex items-start" data-id="element-314">
                      <span className="text-green-600 mr-2" data-id="element-315">✓</span>
                      <span data-id="element-316">Recipients are selected</span>
                    </li>
                    <li className="flex items-start" data-id="element-317">
                      <span className="text-green-600 mr-2" data-id="element-318">✓</span>
                      <span data-id="element-319">Content has been reviewed</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>}
    </div>;
};
export default NewsletterEditor;